import { useState, useRef, useEffect } from "react";
import { useAuth } from "../../../AuthProvider";
import ArrowDownIcon from "../../ui/icons/ArrowDownIcon";
import SignOutIcon from "../../ui/icons/SignOutIcon";

const UserProfile = () => {
  const { userDetails, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get user initials (max 2 characters)
  const getInitials = (name?: string): string => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const handleSignOut = () => {
    setIsOpen(false);
    logout();
  };

  const initials = getInitials(userDetails?.name);
  const fullName = userDetails?.name || "User";
  const email = userDetails?.username || "";

  return (
    <div className="user-profile relative" ref={dropdownRef}>
      {/* Dropdown Trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 cursor-pointer bg-transparent border-none p-0"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* Column 1: User Initials Circle */}
        <div
          style={{
            borderRadius: "7999.2px",
            background: "#171C27",
            color: "#FFF",
            textAlign: "center",
            fontFamily: "Interstate",
            fontSize: "11.302px",
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "16px",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {initials}
        </div>

        {/* Column 2: Name and Email */}
        <div className="flex flex-col items-start">
          <span
            style={{
              color: "var(--color-regentGreen, #171C27)",
              fontFamily: "Interstate",
              fontSize: "12px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "normal",
            }}
          >
            {fullName}
          </span>
          <span
            style={{
              color: "var(--color-charcoal, #63615C)",
              fontFamily: "Interstate",
              fontSize: "12px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "normal",
            }}
          >
            {email}
          </span>
        </div>

        {/* Column 3: Arrow Down Icon */}
        <ArrowDownIcon
          color="#63615C"
          className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[200px] z-[1000]"
        >
          {/* Sign Out Item */}
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 transition-colors cursor-pointer bg-transparent border-none"
          >
            <SignOutIcon color="#E11313" />
            <span
              style={{
                color: "#E11313",
                fontFamily: "var(--sds-typography-body-font-family, Interstate)",
                fontSize: "var(--sds-typography-body-size-medium, 16px)",
                fontStyle: "normal",
                fontWeight: "var(--sds-typography-body-font-weight-regular, 400)",
                lineHeight: "140%",
              }}
            >
              Sign Out
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
